import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import type { FormikHelpers } from 'formik';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import EditorFormik from '~/components/communications/EmailCommunicationCard/EmailEditor/EditorFormik';
import { PERMISSION_ACTIONS, PERMISSION_VERBS, RestrictedPermissions } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import ExposureMultiSelectTextFieldFormik from '~/components/ExposureMultiSelectTextFieldFormik';
import FailedTokensWarningBanner from '~/components/GenericTemplates/FailedTokensWarningBanner';
import GenericTemplateSelectionContainerFormik from '~/components/GenericTemplates/FromTemplate/GenericTemplateSelectionContainerFormik';
import { FROM_TEMPLATE_FORM_KEYS } from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import useNotesConfiguration from '~/components/hooks/useNotesConfiguration';
import NoteIcon from '~/components/icons/NoteIcon';
import { ConfidentialityButton } from '~/components/Note';
import { hasNoteHtmlContent, plainTextToNoteHtml, RICH_NOTE_EDITOR_DISPLAY_CONFIG } from '~/components/Notes/richText';
import TextFieldFormik from '~/components/TextFieldFormik';
import NOTE_HTML_MAX_LENGTH from '~/server_shared/generated-types/NOTE_HTML_MAX_LENGTH';
import { reportAxiosError } from '~/Utils';

import Text from '../core/TextComponents/Text';
import type { Note } from '../Notes/types';

const baseNoteValidation = {
  title: Yup.string().required('Required'),
  exposure_ids: Yup.array().required('Required').min(1, 'Required'),
  [FROM_TEMPLATE_FORM_KEYS.TEMPLATE_ID]: Yup.number().nullable(),
  [FROM_TEMPLATE_FORM_KEYS.TEMPLATE_CONTEXT]: Yup.object().nullable(),
};

const plainNoteValidation = Yup.object().shape({
  ...baseNoteValidation,
  note_text: Yup.string().required('Required'),
});

const richNoteValidation = Yup.object().shape({
  ...baseNoteValidation,
  note_html: Yup.string()
    .test('rich-note-not-empty', 'Required', hasNoteHtmlContent)
    .max(
      NOTE_HTML_MAX_LENGTH,
      `The note is too large - notes are limited to ${NOTE_HTML_MAX_LENGTH / 1_000_000} MB including images`
    ),
});

type NoteFormValues = {
  title: Note['title'];
  note_text: Note['note_text'];
  note_html: string;
  exposure_ids: Note['exposure_ids'];
  is_confidential: Note['is_confidential'];
  [FROM_TEMPLATE_FORM_KEYS.TEMPLATE_ID]: number | null;
  [FROM_TEMPLATE_FORM_KEYS.TEMPLATE_CONTEXT]: Record<string, unknown> | null;
};

const RichNoteEditorFormik: React.FC = () => {
  const { values } = useFormikContext<NoteFormValues>();
  const [editorKey, setEditorKey] = useState(0);
  // A new context object is set on every template pick, including a second pick of the same template.
  const templateContext = values[FROM_TEMPLATE_FORM_KEYS.TEMPLATE_CONTEXT];

  // The template flow writes its HTML to `note_html`; the editor reads its content only when it
  // mounts, so remount it to show the template.
  useEffect(() => {
    if (templateContext) {
      setEditorKey((key) => key + 1);
    }
  }, [templateContext]);

  return (
    <EditorFormik
      key={editorKey}
      id="note_html"
      label="Note"
      displayConfig={RICH_NOTE_EDITOR_DISPLAY_CONFIG}
      classNameEditor="min-h-[120px] max-h-[40vh] overflow-auto"
    />
  );
};

interface CreateEditNoteDialogProps {
  notesThreadId?: number;
  onClose: () => void;
  onSubmitNote: (noteId: number) => Promise<void> | void;
  defaultExposureIds?: number[];
  initialTitle?: string;
  initialNoteText?: string;
  open?: boolean;
  disableMinimized?: boolean;
  onMinimized?: () => void;
  note?: Note;
}

const CreateEditNoteDialog: React.FC<CreateEditNoteDialogProps> = (props) => {
  const {
    onSubmitNote,
    onClose,
    defaultExposureIds,
    initialTitle = '',
    initialNoteText = '',
    open = true,
    onMinimized,
    disableMinimized,
    note,
  } = props;

  const { claim } = useClaim({ claimRequired: true });
  const { configuration: notesConfiguration, isLoading: isNotesConfigurationLoading } = useNotesConfiguration();

  const [isFromTemplateDialogOpen, setIsFromTemplateDialogOpen] = useState(false);
  const [failedTokens, setFailedTokens] = useState(null);

  const { classes } = useStyles();
  const isEdit = !!note;
  // Which note field to show depends on the configuration; wait for it rather than swap the field under
  // the user's typing. If it fails to load, the plain field is shown.
  const isWaitingForConfiguration = !note?.is_rich_text && !notesConfiguration && isNotesConfigurationLoading;
  // A rich note is always edited as rich text; the toggle decides whether new rich content is made. The
  // choice is fixed once the dialog shows a field: a cached configuration is shown first and the refetch
  // behind it may flip the toggle, which would drop what the user has typed into the other field.
  const isRichTextRef = useRef<boolean | null>(null);
  if (isRichTextRef.current === null && !isWaitingForConfiguration) {
    isRichTextRef.current = !!note?.is_rich_text || !!notesConfiguration?.is_rich_text_notes_enabled;
  }
  const isRichText = !!isRichTextRef.current;

  const handleOpenFromTemplateDialog = () => {
    setIsFromTemplateDialogOpen(true);
  };

  const handleCloseFromTemplateDialog = () => {
    setIsFromTemplateDialogOpen(false);
  };
  const handleSubmit = async (values: NoteFormValues, formikHelpers: FormikHelpers<NoteFormValues>) => {
    try {
      const method = isEdit ? 'put' : 'post';
      const url = `/api/v1/claims/${claim.id}/notes${isEdit ? `/${note.id}` : ''}`;
      // The server derives a rich note's plain text from its HTML, so only one of the two is sent.
      const { note_text, note_html, ...rest } = values;
      const payload = isRichText ? { ...rest, note_html } : { ...rest, note_text };
      const { data }: { data: { note_id: number } } = await axios(url, { method, data: payload });
      await onSubmitNote(data.note_id);
    } catch (error) {
      await reportAxiosError(error);
      formikHelpers.setSubmitting(false);
    }
  };

  if (isWaitingForConfiguration) {
    return null;
  }

  return (
    <Formik<NoteFormValues>
      initialValues={{
        title: note?.title || initialTitle,
        note_text: note?.note_text || initialNoteText,
        note_html: note?.is_rich_text ? note.note_html || '' : plainTextToNoteHtml(note?.note_text || initialNoteText),
        exposure_ids: note?.exposure_ids || defaultExposureIds || [],
        is_confidential: note?.is_confidential || false,
        [FROM_TEMPLATE_FORM_KEYS.TEMPLATE_ID]: null,
        [FROM_TEMPLATE_FORM_KEYS.TEMPLATE_CONTEXT]: null,
      }}
      validationSchema={isRichText ? richNoteValidation : plainNoteValidation}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikProps) => (
        <CardDialog
          open={open}
          isDialog
          title={isEdit ? 'Edit Note' : 'Create Note'}
          maxWidth={isRichText ? 'md' : 'sm'}
          onClose={onClose}
          preventClose={formikProps.isSubmitting}
          onMinimized={onMinimized}
          disableMinimized={disableMinimized}
          action={
            !isEdit ? (
              <RestrictedPermissions action={PERMISSION_ACTIONS.NOTE} verb={PERMISSION_VERBS.FULL}>
                <ConfidentialityButton
                  isConfidential={formikProps.values['is_confidential'] as boolean}
                  onChangeConfidentiality={() =>
                    formikProps.setFieldValue('is_confidential', !formikProps.values['is_confidential'])
                  }
                />
              </RestrictedPermissions>
            ) : null
          }
          footerActions={
            <DialogFooterActions
              primaryLabel={isEdit ? 'Update' : 'Create'}
              onClickPrimary={formikProps.handleSubmit}
              disabled={formikProps.isSubmitting}
              showSecondary={false}
            />
          }
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <div className="flex justify-between">
                <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD}>
                  <span className="flex inline-flex items-center">
                    <NoteIcon iconColor="inherit" size={20} />
                    <span className="mx-8">Note</span>
                    {claim.claim_id_display}
                  </span>
                </Text>
                {!isEdit ? (
                  <div className="flex items-center justify-end">
                    <Button color="primary" onClick={handleOpenFromTemplateDialog}>
                      <AddIcon />
                      From template
                    </Button>
                  </div>
                ) : null}
              </div>
            </Grid>
            <Grid item xs={12}>
              <TextFieldFormik id="title" label="Title" fullWidth className={classes.textField} disabled={isEdit} />
            </Grid>
            <Grid item xs={12}>
              {isRichText ? (
                <RichNoteEditorFormik />
              ) : (
                <TextFieldFormik id="note_text" label="Note" className={classes.textField} fullWidth multiline />
              )}
            </Grid>
            <Grid item xs={12}>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore*/}
              <FailedTokensWarningBanner failedTokens={failedTokens} />
            </Grid>
            <Grid item xs={12}>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore*/}
              <ExposureMultiSelectTextFieldFormik claim={claim} />
            </Grid>
          </Grid>
          {isFromTemplateDialogOpen ? (
            <GenericTemplateSelectionContainerFormik
              handleClose={handleCloseFromTemplateDialog}
              templateType="note"
              titleFieldId="title"
              bodyFieldId={isRichText ? 'note_html' : 'note_text'}
              shouldConvertBodyHtmlToText={!isRichText}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              setFailedTokens={setFailedTokens}
            />
          ) : null}
        </CardDialog>
      )}
    </Formik>
  );
};

export default CreateEditNoteDialog;
